import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { goalService } from '../services/goalService';
import { useTranslation } from '../i18n';
import { useToast } from './ToastContext';
import {
  advance,
  loadSession,
  PomodoroSession,
  remainingSeconds,
  saveSession,
  startSession,
} from '../utils/pomodoro';
import { logger } from '../utils/logger';
import { trackEvent } from '../utils/analytics';

interface PomodoroContextValue {
  session: PomodoroSession | null;
  /** Seconds left in the current phase (0 when idle) */
  remaining: number;
  start: (todoId: string, title: string) => void;
  stop: () => void;
  /** Changes whenever a focus phase was counted, so pages can reload the count */
  lastCompleted: { todoId: string; at: number } | null;
}

const PomodoroContext = createContext<PomodoroContextValue | null>(null);

export const PomodoroProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [session, setSession] = useState<PomodoroSession | null>(loadSession);
  const [now, setNow] = useState(() => Date.now());
  const [lastCompleted, setLastCompleted] = useState<PomodoroContextValue['lastCompleted']>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const tRef = useRef(t);
  tRef.current = t;
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const applySession = useCallback((next: PomodoroSession | null) => {
    sessionRef.current = next;
    setSession(next);
    saveSession(next);
  }, []);

  const countFocus = useCallback(async (finished: PomodoroSession) => {
    const message = tRef.current('pomodoro.focusDone', { title: finished.title });
    toastRef.current.success(message);
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(tRef.current('pomodoro.notificationTitle'), { body: message });
    }
    try {
      await goalService.completePomodoro(finished.todoId);
      trackEvent('pomodoro_completed', {});
      setLastCompleted({ todoId: finished.todoId, at: Date.now() });
    } catch (err) {
      logger.error('pomodoro.countFailed', err);
      toastRef.current.error(tRef.current('pomodoro.countFailed'));
    }
  }, []);

  useEffect(() => {
    if (!session) return undefined;
    const tick = () => {
      const current = sessionRef.current;
      if (!current) return;
      const at = Date.now();
      setNow(at);
      const result = advance(current, at);
      if (result.session !== current) {
        applySession(result.session);
        if (result.focusCompleted) void countFocus(current);
      }
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
    // Only (re)start the ticker when a session starts or ends, not on every phase change
  }, [!!session, applySession, countFocus]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback((todoId: string, title: string) => {
    applySession(startSession(todoId, title));
    setNow(Date.now());
    if ('Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  }, [applySession]);

  const stop = useCallback(() => applySession(null), [applySession]);

  const value = useMemo<PomodoroContextValue>(() => ({
    session,
    remaining: session ? remainingSeconds(session, now) : 0,
    start,
    stop,
    lastCompleted,
  }), [session, now, start, stop, lastCompleted]);

  return <PomodoroContext.Provider value={value}>{children}</PomodoroContext.Provider>;
};

export const usePomodoro = (): PomodoroContextValue => {
  const ctx = useContext(PomodoroContext);
  if (!ctx) throw new Error('usePomodoro must be used within PomodoroProvider');
  return ctx;
};
